// Reading what the first-run gate offers, and settling it.
//
// One read and one write, and the write happens once in an account's life. It
// is a hook rather than state inside the screen for the reason every other read
// in this client is: the screen is mounted and unmounted by `App.tsx`, and a
// request that lived in the component would be re-sent by a re-render the
// person did not ask for.
//
// IT READS NOTHING UNTIL THE GATE IS UP (`active`), exactly as `useBoard` reads
// nothing until the board is the screen that is on. The day payload already said
// whether the choice is open, so somebody who answered it months ago never pays
// for this request at all.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isSignedOut, messageFor, refusalRule } from '../api/client.ts';
import type { FirstRunExample } from '../api/client.ts';

/** The refusal that means somebody else already answered. Never a failure. */
const ALREADY_CHOSEN = 'first-run-already-chosen';

export interface FirstRunState {
  /** What the button would write. Null until the read lands. */
  examples: FirstRunExample[] | null;
  /** The first word of their name, or '' when there is none to greet them by. */
  name: string;
  /** Why the preview could not be read. The screen offers another go. */
  problem: string | null;
  /** A choice is in flight. Both buttons are held while one is. */
  busy: boolean;
  /** Why the choice did not land, beside the buttons that did not work. */
  failure: string | null;
  /** Ask for the preview again after a failed read. */
  retry: () => void;
  /** Settle it. `true` writes the examples; `false` starts empty. */
  choose: (seed: boolean) => void;
}

export interface FirstRunHost {
  /** Whether the gate is up. Nothing is read while it is not. */
  active: boolean;
  /**
   * The choice is settled - re-read the day, which is what lowers the gate.
   *
   * Deliberately not "hide the gate": the gate comes down when a day payload
   * says `firstRun` is false, and that payload is also the one carrying the
   * content that was just written. Lowering it here instead would put an empty
   * day on screen for as long as the re-read took, on the one screen whose
   * whole purpose is that a new account does not open on an empty day.
   */
  onSettled: () => void;
  /** A 401 raises the signed-out gate, as it does from every other read here. */
  onSignedOut: () => void;
}

export function useFirstRun({ active, onSettled, onSignedOut }: FirstRunHost): FirstRunState {
  const [examples, setExamples] = useState<FirstRunExample[] | null>(null);
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Fresh closures every render, so the effect must not depend on them: it would
  // re-read the preview every time anything in the shell re-rendered.
  const latest = useRef({ onSettled, onSignedOut });
  latest.current = { onSettled, onSignedOut };

  // WHETHER AN ANSWER IS ALREADY OUT - a ref, and the `busy` state beside it is
  // only for drawing. `setBusy(true)` does not change the `busy` this render's
  // `choose` closed over, so two calls in ONE tick both read false and both go
  // out; a ref changes within the commit, which is exactly the difference
  // needed. `useDayScreen` keeps its `inFlight` set for the same reason, so this
  // is one pattern in this client and not two.
  //
  // It is deliberately never lowered on success: the choice is settled for good
  // and the gate is about to be replaced by the day.
  const answering = useRef(false);

  useEffect(() => {
    if (!active) return;
    let live = true;

    void (async () => {
      try {
        const payload = await api.firstRun();
        if (!live) return;
        // ANOTHER TAB GOT THERE FIRST. The day payload said the choice was open
        // and this one says it is not, which is not a failure and must not be
        // drawn as one: the state the person is about to ask for already holds.
        // Re-reading the day is what takes the gate down and brings whatever the
        // other tab chose with it.
        if (!payload.pending) {
          latest.current.onSettled();
          return;
        }
        setExamples(payload.examples);
        setName(payload.name);
        setProblem(null);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current.onSignedOut();
          return;
        }
        if (live) setProblem(messageFor(error));
      }
    })();

    return () => {
      live = false;
    };
  }, [active, attempt]);

  const retry = useCallback(() => {
    setProblem(null);
    setAttempt((n) => n + 1);
  }, []);

  const choose = useCallback((seed: boolean) => {
    // Held from the press rather than from the response: the two buttons write
    // opposite things and only one of them may ever land, so a second press
    // while the first is in flight must not be able to reach the server. The
    // server refuses it too (the claim in `settleFirstRun`), which is what makes
    // this a courtesy rather than the guarantee - but a client that let the
    // second one out has already been wrong, and there is no undo for a doubled
    // seed.
    if (answering.current) return;
    answering.current = true;
    setBusy(true);
    setFailure(null);

    void (async () => {
      try {
        await api.chooseFirstRun(seed);
        // Neither `busy` nor the ref is lowered on the way out. The choice is
        // settled for good and the gate stays up until the day that follows this
        // write has been read back - a pair of live buttons over a screen about
        // to be replaced is a second choice somebody can press.
        latest.current.onSettled();
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current.onSignedOut();
          return;
        }
        // ALREADY ANSWERED, which is the ordinary shape of a retry after a
        // response was lost in transit and of a second tab. The state asked
        // for holds, so this goes on to the day exactly as a success does -
        // reporting it as a failure would leave somebody staring at a gate
        // over an account that is already set up.
        if (refusalRule(error) === ALREADY_CHOSEN) {
          latest.current.onSettled();
          return;
        }
        // Nothing was written, so nothing is settled: both halves of the hold
        // come off together, or the buttons are drawn live over a hook that
        // will refuse them.
        answering.current = false;
        setBusy(false);
        setFailure(messageFor(error));
      }
    })();
  }, []);

  return { examples, name, problem, busy, failure, retry, choose };
}
